import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { ApiError } from "@/shared/utils/apiError";

type ErrorState = { title: string; message: string } | null;
const Ctx = createContext<{
  showApiError: (err: unknown, opts?: { title?: string }) => void;
} | null>(null);

export function ErrorDialogProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<ErrorState>(null);
  const showApiError = useCallback(
    (err: unknown, opts?: { title?: string }) => {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Something went wrong.";
      setError({ title: opts?.title ?? "Fel", message });
    },
    [],
  );

  return (
    <Ctx.Provider value={{ showApiError }}>
      {children}
      <AlertDialog
        open={error !== null}
        onOpenChange={(o) => !o && setError(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{error?.title}</AlertDialogTitle>
            <AlertDialogDescription>{error?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setError(null)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Ctx.Provider>
  );
}

export function useErrorDialog() {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error("useErrorDialog must be used within ErrorDialogProvider");
  return ctx;
}
